import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { BACKEND_URL } from "../api";
import { toast } from "sonner";
import {
  X, ArrowLeft, CheckCircle, CircleNotch, Eraser, PenNib, ShieldCheck, ArrowRight,
  Download, FloppyDisk,
} from "@phosphor-icons/react";

/**
 * Phase B — Sign Now (tablet-first) + In-PDF Editor.
 *
 * Flow:
 *   1. Admin clicks "Generate Document" → picks template + service address → /sign/:did
 *   2. This page renders each PDF page as an image overlay with HTML inputs
 *      precisely positioned on top of every detected AcroForm field rect.
 *   3. User taps a field, types, the value auto-saves (PATCH /variables, debounced).
 *      The page image refreshes after each save so the printed PDF reflects edits.
 *   4. Customer + ZMEX rep sign on three pads (Initials, Customer, ZMEX).
 *   5. Submit → POST /sign → success screen with the final signed PDF.
 */

function authHeader() {
  const t = localStorage.getItem("rl_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function fetchPdfBlob(path) {
  const r = await fetch(`${BACKEND_URL}/api${path}`, { headers: authHeader() });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.blob();
}

// PNG page-image scale (used as both backend rasterization factor and the
// pixel multiplier for our coord transform).
const PAGE_RENDER_SCALE = 2;

const PADS = [
  {
    key: "customer_initial",
    title: "Customer Initials",
    subtitle: "Tap and draw your initials below",
    bodyKey: "customer_initials",
    height: 130,
    required: true,
  },
  {
    key: "customer_signature",
    title: "Customer Signature",
    subtitle: "Please sign your full name below",
    bodyKey: "customer_signature",
    height: 190,
    required: true,
  },
  {
    key: "zmex_signature",
    title: "ZMEX Representative Signature",
    subtitle: "ZMEX sales rep — sign to confirm",
    bodyKey: "zmex_signature",
    height: 190,
    required: true,
  },
];

/** Single signature pad — HTML canvas with touch + mouse + pen support. */
function SignaturePad({ height = 200, value, onChange, testid }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPtRef = useRef(null);
  const hasInkRef = useRef(false);

  const fitCanvas = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    if (rect.width === 0) return;
    c.width = Math.round(rect.width * dpr);
    c.height = Math.round(rect.height * dpr);
    const ctx = c.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.6;
    ctx.strokeStyle = "#000000";
    if (value) {
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, rect.width, rect.height);
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
      };
      img.src = value;
    } else {
      ctx.clearRect(0, 0, rect.width, rect.height);
    }
  }, [value]);

  useEffect(() => {
    fitCanvas();
    const onResize = () => fitCanvas();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function pointerPos(e) {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    if (e.touches && e.touches.length) {
      const t = e.touches[0];
      return { x: t.clientX - rect.left, y: t.clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function start(e) { e.preventDefault(); drawingRef.current = true; lastPtRef.current = pointerPos(e); }
  function move(e) {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pointerPos(e);
    ctx.beginPath();
    ctx.moveTo(lastPtRef.current.x, lastPtRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPtRef.current = p;
    hasInkRef.current = true;
  }
  function end(e) {
    if (!drawingRef.current) return;
    e.preventDefault();
    drawingRef.current = false;
    if (hasInkRef.current) onChange(canvasRef.current.toDataURL("image/png"));
  }
  function clear() {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const rect = c.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);
    hasInkRef.current = false;
    onChange("");
  }

  return (
    <div className="relative">
      <canvas
        ref={canvasRef}
        className="block w-full bg-white rounded-sm border-2 border-dashed border-[#012340]/25 touch-none cursor-crosshair"
        style={{ height }}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        onPointerDown={(e) => e.target.setPointerCapture?.(e.pointerId)}
        data-testid={`${testid}-canvas`}
      />
      <button type="button" onClick={clear} data-testid={`${testid}-clear`}
        className="absolute top-2 right-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-sm bg-white/90 border border-[#012340]/15 text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C738A] hover:text-[#E53935] hover:border-[#E53935]/30">
        <Eraser size={12} weight="bold" /> Clear
      </button>
      {!value && (
        <div className="absolute inset-0 grid place-items-center pointer-events-none">
          <div className="text-[#012340]/20 font-heading italic text-lg select-none">Sign here</div>
        </div>
      )}
    </div>
  );
}

/** Heuristic — is this PDF field a signature/initial slot we render as an image, not a text input? */
function isSigningField(name) {
  const n = (name || "").toLowerCase();
  if (n.includes("initial")) return true;
  if (/\b(zmex|company|representative|rep[_\s-]?sig|sales|agent|hauler)\b/.test(n)) {
    if (/sig|signature|sign(ed)?/.test(n)) return true;
  }
  if (/signature|sign(ed)?_?by/.test(n)) return true;
  return false;
}

/** Pretty label for a PDF field name. */
function prettifyFieldName(name) {
  return (name || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b([a-z])/g, (m) => m.toUpperCase());
}

/** One page of the PDF rendered as an <img> with overlay <input> elements for each field. */
function PdfPageEditor({ did, pageIndex, pageWidthPt, pageHeightPt, fields, values, onChange, editable, reloadTick }) {
  const wrapRef = useRef(null);
  const [wrapWidth, setWrapWidth] = useState(0);

  // Track the wrapper's measured width — used to translate PDF user-space
  // coords (origin bottom-left, in points) into CSS px (origin top-left).
  useEffect(() => {
    function measure() {
      if (wrapRef.current) setWrapWidth(wrapRef.current.clientWidth);
    }
    measure();
    const ro = new ResizeObserver(measure);
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  const scale = wrapWidth && pageWidthPt ? wrapWidth / pageWidthPt : 0;
  const pageH = pageHeightPt * scale;

  // The page image is fetched via fetch (we need auth headers); wrap into a
  // blob URL on every reloadTick so edits visually refresh.
  const [imgUrl, setImgUrl] = useState(null);
  useEffect(() => {
    let revoke = null;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/documents/${did}/page/${pageIndex}.png?scale=${PAGE_RENDER_SCALE}`, {
          headers: authHeader(),
        });
        if (!r.ok) throw new Error("page fetch failed");
        const blob = await r.blob();
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        revoke = url;
        setImgUrl(url);
      } catch {
        setImgUrl(null);
      }
    })();
    return () => { cancelled = true; if (revoke) URL.revokeObjectURL(revoke); };
  }, [did, pageIndex, reloadTick]);

  const pageFields = fields.filter((f) => f.page_index === pageIndex && f.rect);

  return (
    <div ref={wrapRef} className="relative w-full bg-white shadow-sm border border-[#012340]/10" data-testid={`pdf-page-${pageIndex}`}>
      {imgUrl ? (
        <img src={imgUrl} alt={`Page ${pageIndex + 1}`} style={{ width: "100%", height: pageH || "auto", display: "block" }} />
      ) : (
        <div style={{ width: "100%", height: pageH || 800 }} className="grid place-items-center text-[#5C738A] text-sm">
          <span><CircleNotch className="animate-spin mr-2 inline" /> Rendering page {pageIndex + 1}…</span>
        </div>
      )}
      {/* Overlay inputs absolute-positioned over each field rect. */}
      {scale > 0 && pageFields.map((f) => {
        const [x0, y0, x1, y1] = f.rect;
        const cssLeft = x0 * scale;
        const cssTop = (pageHeightPt - y1) * scale;
        const cssWidth = (x1 - x0) * scale;
        const cssHeight = (y1 - y0) * scale;
        if (isSigningField(f.name)) {
          // Show a dashed "Signs on submit" placeholder so the user knows
          // signatures land here.
          return (
            <div key={f.name}
              className="absolute border border-dashed border-[#CFAB7A]/70 bg-[#CFAB7A]/10 rounded-sm pointer-events-none flex items-center justify-center"
              style={{ left: cssLeft, top: cssTop, width: cssWidth, height: cssHeight }}
              title="Signature will be placed here on submit"
              data-testid={`pdf-sig-slot-${f.name}`}>
              <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#CFAB7A] truncate px-1">Sig: {f.name}</span>
            </div>
          );
        }
        const val = values[f.name] ?? "";
        const fontSize = Math.max(10, Math.min(15, cssHeight * 0.7));
        return (
          <input
            key={f.name}
            type="text"
            value={val}
            disabled={!editable}
            onChange={(e) => onChange(f.name, e.target.value)}
            placeholder={editable ? prettifyFieldName(f.name) : ""}
            data-testid={`pdf-field-${f.name}`}
            className="absolute bg-[#FFF9E6]/85 hover:bg-[#FFF1B8]/95 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#CFAB7A] border border-[#CFAB7A]/40 focus:border-[#CFAB7A] rounded-[2px] text-[#012340]"
            style={{
              left: cssLeft, top: cssTop, width: cssWidth, height: cssHeight,
              fontSize, lineHeight: `${cssHeight}px`, padding: "0 4px",
            }}
          />
        );
      })}
    </div>
  );
}

export default function TabletSigningView() {
  const { did } = useParams();
  const navigate = useNavigate();

  const [doc, setDoc] = useState(null);
  const [fieldsMeta, setFieldsMeta] = useState(null); // {fields, values, page_sizes, editable}
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signedPdfUrl, setSignedPdfUrl] = useState(null);
  const [signatures, setSignatures] = useState({
    customer_initials: "",
    customer_signature: "",
    zmex_signature: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [signedDoc, setSignedDoc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [reloadTick, setReloadTick] = useState(0); // bump to re-render page PNGs

  // Local optimistic values map keyed by PDF field name.
  const [values, setValues] = useState({});

  // Initial load — document + fields metadata + values.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [docR, fieldsR] = await Promise.all([
          api.get(`/documents/${did}`),
          api.get(`/documents/${did}/fields`),
        ]);
        if (cancelled) return;
        const docData = docR.data;
        const fieldsData = fieldsR.data;
        setDoc(docData);
        setFieldsMeta(fieldsData);
        setValues(fieldsData.values || {});
        if (docData.status === "signed") {
          setSignedDoc(docData);
          try {
            const blob = await fetchPdfBlob(`/documents/${did}/preview`);
            if (!cancelled) setSignedPdfUrl(URL.createObjectURL(blob));
          } catch {}
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || "Could not load document");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [did]);

  // Debounced auto-save of pending field edits.
  const pendingRef = useRef({});         // accumulates {fieldName: value} between flushes
  const flushTimerRef = useRef(null);
  const flushInFlightRef = useRef(false);

  const flushPending = useCallback(async () => {
    if (flushInFlightRef.current) return;            // someone is already saving
    const payload = pendingRef.current;
    if (!payload || Object.keys(payload).length === 0) return;
    pendingRef.current = {};
    flushInFlightRef.current = true;
    setSaving(true);
    try {
      const { data } = await api.patch(`/documents/${did}/variables`, { values: payload });
      // Sync server values back in case backend normalized anything.
      setValues((prev) => ({ ...prev, ...(data?.values || payload) }));
      setLastSavedAt(new Date());
      setReloadTick((t) => t + 1); // refresh the page PNGs to show the new text
    } catch (err) {
      // Put values back into pending so we'll retry on the next flush.
      pendingRef.current = { ...payload, ...pendingRef.current };
      toast.error(err.response?.data?.detail || "Auto-save failed — will retry");
    } finally {
      flushInFlightRef.current = false;
      setSaving(false);
      // If new edits came in while we were saving, re-arm a flush.
      if (Object.keys(pendingRef.current).length > 0) {
        flushTimerRef.current && clearTimeout(flushTimerRef.current);
        flushTimerRef.current = setTimeout(flushPending, 600);
      }
    }
  }, [did]);

  const onFieldChange = useCallback((name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    pendingRef.current[name] = value;
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    flushTimerRef.current = setTimeout(flushPending, 600);
  }, [flushPending]);

  // Flush on unmount + before signing.
  useEffect(() => () => {
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    if (Object.keys(pendingRef.current).length > 0) {
      // Fire-and-forget; we can't await in cleanup, but the save attempt goes
      // out via fetch and the server will catch it.
      api.patch(`/documents/${did}/variables`, { values: pendingRef.current }).catch(() => {});
    }
  }, [did]);

  const allRequiredCaptured = useMemo(
    () => PADS.filter((p) => p.required).every((p) => !!signatures[p.bodyKey]),
    [signatures]
  );

  async function submit() {
    if (!allRequiredCaptured) {
      toast.error("Please complete every signature (Initials, Customer, ZMEX)");
      return;
    }
    // First flush any pending edits so the signed PDF has the latest values.
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    if (Object.keys(pendingRef.current).length > 0) {
      try { await flushPending(); } catch {}
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/documents/${did}/sign`, signatures);
      toast.success("Signed · saved");
      setSignedDoc(data);
      try {
        const blob = await fetchPdfBlob(`/documents/${did}/preview`);
        setSignedPdfUrl(URL.createObjectURL(blob));
      } catch {}
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signing failed");
    } finally {
      setSubmitting(false);
    }
  }

  async function downloadFinal() {
    try {
      const blob = await fetchPdfBlob(`/documents/${did}/download`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = signedDoc?.signed_filename || signedDoc?.generated_filename || "document.pdf";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  }

  function exit() {
    navigate("/admin/documents");
  }

  // ----------- Success / signed screen -----------
  if (signedDoc) {
    return (
      <div className="fixed inset-0 z-[100] bg-[#FAFAFA] flex flex-col" data-testid="tablet-sign-success">
        <header className="flex-shrink-0 bg-[#012340] text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 grid place-items-center bg-[#2E7D32]/25 rounded-full">
              <CheckCircle size={20} weight="fill" className="text-[#7CC982]" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#7CC982]">Signed</div>
              <div className="font-heading text-base font-bold truncate max-w-[60vw]">
                {signedDoc.variables_snapshot?.business_name || signedDoc.template_name}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={downloadFinal} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-sm bg-white/10 hover:bg-white/20 text-xs font-bold uppercase tracking-[0.2em]" data-testid="tablet-sign-download">
              <Download size={14} weight="bold" /> Download
            </button>
            <button onClick={exit} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm bg-[#CFAB7A] text-[#012340] text-xs font-bold uppercase tracking-[0.2em] hover:brightness-105" data-testid="tablet-sign-done">
              Done <ArrowRight size={14} weight="bold" />
            </button>
          </div>
        </header>
        <main className="flex-1 min-h-0 p-4">
          {signedPdfUrl ? (
            <iframe src={signedPdfUrl} title="Signed Document" className="w-full h-full bg-white border border-[#012340]/10 rounded-sm" data-testid="signed-pdf-iframe" />
          ) : (
            <div className="w-full h-full grid place-items-center text-sm text-[#5C738A]">
              <CircleNotch className="animate-spin mr-2 inline" /> Loading signed PDF…
            </div>
          )}
        </main>
      </div>
    );
  }

  // ----------- Editor + signing screen -----------
  const fields = fieldsMeta?.fields || [];
  const pageSizes = fieldsMeta?.page_sizes || [];
  const editable = !!fieldsMeta?.editable;

  return (
    <div className="fixed inset-0 z-[100] bg-[#FAFAFA] flex flex-col" data-testid="tablet-sign-view">
      <header className="flex-shrink-0 bg-[#012340] text-white px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
        <button onClick={exit} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-white/8 hover:bg-white/15 text-xs font-bold uppercase tracking-[0.2em]" data-testid="tablet-sign-cancel">
          <ArrowLeft size={14} weight="bold" /> Cancel
        </button>
        <div className="min-w-0 text-center flex-1">
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#CFAB7A]">Fill & Sign on Tablet</div>
          <div className="font-heading text-sm sm:text-base font-bold truncate" data-testid="tablet-sign-title">
            {doc?.template_name || "Document"} · {doc?.variables_snapshot?.service_address || ""}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="hidden sm:inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.22em] text-white/70" data-testid="autosave-status">
            {saving ? (<><CircleNotch className="animate-spin" size={12} /> Saving…</>) :
              lastSavedAt ? (<><FloppyDisk size={12} weight="bold" className="text-[#7CC982]" /> Saved</>) :
              (<>Ready</>)}
          </span>
          <button onClick={submit} disabled={!allRequiredCaptured || submitting}
            className="inline-flex items-center gap-1.5 px-4 sm:px-5 py-2 rounded-sm bg-[#CFAB7A] text-[#012340] text-xs font-bold uppercase tracking-[0.2em] hover:brightness-105 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="tablet-sign-submit">
            {submitting ? (<><CircleNotch className="animate-spin" size={14} /> Signing…</>) :
              (<><ShieldCheck size={14} weight="bold" /> Submit Signed</>)}
          </button>
        </div>
      </header>

      <main className="flex-1 min-h-0 flex flex-col lg:flex-row overflow-hidden">
        {/* PDF editor */}
        <section className="flex-1 min-h-0 min-w-0 lg:w-3/5 overflow-y-auto" data-testid="tablet-sign-editor">
          <div className="p-3 sm:p-4 space-y-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C738A] flex items-center justify-between flex-wrap gap-2">
              <span>Fill the document — tap any field and type. Changes auto-save.</span>
              <span className="text-[#012340]">Status: {(doc?.status || "—").toUpperCase()}</span>
            </div>
            {loading && (
              <div className="w-full grid place-items-center py-20 text-sm text-[#5C738A]">
                <span><CircleNotch className="animate-spin mr-2 inline" /> Loading document…</span>
              </div>
            )}
            {error && <div className="text-sm text-[#E53935] py-10 text-center">{error}</div>}
            {!loading && !error && pageSizes.map((sz, idx) => (
              <PdfPageEditor
                key={idx}
                did={did}
                pageIndex={idx}
                pageWidthPt={sz[0]}
                pageHeightPt={sz[1]}
                fields={fields}
                values={values}
                onChange={onFieldChange}
                editable={editable}
                reloadTick={reloadTick}
              />
            ))}
          </div>
        </section>

        {/* Signature pads */}
        <aside className="lg:w-2/5 max-h-[58vh] lg:max-h-none overflow-y-auto border-t lg:border-t-0 lg:border-l border-[#012340]/10 bg-white p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-4">
            <PenNib size={18} weight="fill" className="text-[#CFAB7A]" />
            <h2 className="font-heading text-base font-bold text-[#012340]">Capture Signatures</h2>
          </div>
          <ol className="space-y-5" data-testid="tablet-sign-pads">
            {PADS.map((pad, idx) => {
              const value = signatures[pad.bodyKey];
              const captured = !!value;
              return (
                <li key={pad.key} className="space-y-2" data-testid={`tablet-sign-pad-${pad.key}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 grid place-items-center rounded-full text-[10px] font-bold ${captured ? "bg-[#2E7D32] text-white" : "bg-[#012340]/10 text-[#012340]"}`}>
                        {captured ? <CheckCircle size={14} weight="fill" /> : idx + 1}
                      </div>
                      <div>
                        <div className="text-sm font-bold text-[#012340]">{pad.title}</div>
                        <div className="text-[11px] text-[#5C738A]">{pad.subtitle}</div>
                      </div>
                    </div>
                    {pad.required && (
                      <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#E53935]">Required</span>
                    )}
                  </div>
                  <SignaturePad
                    height={pad.height}
                    value={value}
                    onChange={(v) => setSignatures((s) => ({ ...s, [pad.bodyKey]: v }))}
                    testid={`tablet-sign-pad-${pad.key}`}
                  />
                </li>
              );
            })}
          </ol>

          <div className="mt-6 pt-4 border-t border-[#012340]/10 text-[11px] text-[#5C738A]">
            <ShieldCheck size={12} weight="bold" className="inline mr-1 -mt-0.5 text-[#2E7D32]" />
            Filled values + signatures are baked into the final PDF on submit.
          </div>

          <button onClick={submit} disabled={!allRequiredCaptured || submitting}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-sm bg-[#CFAB7A] text-[#012340] text-sm font-bold uppercase tracking-[0.2em] hover:brightness-105 disabled:opacity-40 disabled:cursor-not-allowed lg:hidden"
            data-testid="tablet-sign-submit-mobile">
            {submitting ? (<><CircleNotch className="animate-spin" size={14} /> Signing…</>) :
              (<><ShieldCheck size={14} weight="bold" /> Submit Signed Document</>)}
          </button>
        </aside>
      </main>
    </div>
  );
}
